/**
 * Places domain: resolve the effective use-detail requirements for an occupancy
 * classification (the profile-form field set + levels).
 *
 * Reads the EXPLICIT, materialized relationship
 * (place_classification_detail_requirements) — NOT a live BEDES query. The BEDES
 * edge tool only SEEDS that table (docs/places/phase-2.md M2.5); at runtime this
 * resolver just merges the dual-scope rows:
 *   * global rows   = the organizational default relationship + level
 *   * org rows      = customization: override level, hide (is_hidden), or add
 * Org rows shadow global on (occupancy, definition); hidden rows drop out.
 * NB an org is_hidden row is honored even when no global row currently matches
 * (a reseed may re-add the global later — the org's stated intent is to hide).
 */
import { z } from "zod";
/** Guards `orgId` before it is interpolated into a PostgREST `.or()` filter — a
 * non-UUID value could inject extra filter clauses. */
const uuidSchema = z.string().uuid();
// One literal (NOT concatenated strings — TS would widen to `string` and
// postgrest-js would type rows as GenericStringError). The to-one embed types
// as a single object via the definition_id FK relationship.
const EFFECTIVE_SELECT = "occupancy_classification, definition_id, organization_id, requirement_level, is_hidden, sort_order, definition:place_detail_definitions!inner(key, name, data_type, unit, is_active)";
/** Merge the dual-scope rows of ONE classification (org shadows global on
 * definition_id; hidden rows drop) into the sorted effective field set. */
function mergeEffectiveRows(rows) {
    const byDef = new Map();
    for (const row of rows) {
        const slot = byDef.get(row.definition_id) ?? {};
        if (row.organization_id == null)
            slot.global = row;
        else
            slot.org = row;
        byDef.set(row.definition_id, slot);
    }
    const out = [];
    for (const [definitionId, { global, org }] of byDef) {
        if (org?.is_hidden)
            continue; // org suppressed this relationship
        const row = org ?? global;
        if (!row || !row.definition || row.definition.is_active === false)
            continue;
        out.push({
            definitionId,
            key: row.definition.key,
            name: row.definition.name,
            dataType: row.definition.data_type,
            unit: row.definition.unit,
            requirementLevel: org?.requirement_level ??
                global?.requirement_level ??
                "optional",
            sortOrder: org?.sort_order ?? global?.sort_order ?? 0,
            isOrgScoped: org != null,
        });
    }
    return out.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
}
/**
 * The effective requirements for SEVERAL occupancy classifications at once —
 * one query, not one per classification (the profile-form endpoint resolves a
 * mixed-use venue's whole field surface this way). Every requested
 * classification is present in the returned map, empty array when unmapped.
 */
export async function getEffectiveClassificationRequirementsMap(client, { occupancyClassifications, orgId, }) {
    if (!uuidSchema.safeParse(orgId).success) {
        throw new Error(`getEffectiveClassificationRequirementsMap: invalid orgId`);
    }
    const distinct = [...new Set(occupancyClassifications)];
    const result = new Map(distinct.map((occ) => [occ, []]));
    if (distinct.length === 0)
        return result;
    const { data, error } = await client
        .from("place_classification_detail_requirements")
        .select(EFFECTIVE_SELECT)
        .in("occupancy_classification", distinct)
        .or(`organization_id.is.null,organization_id.eq.${orgId}`);
    if (error)
        throw error;
    const byOccupancy = new Map();
    for (const row of (data ?? [])) {
        const bucket = byOccupancy.get(row.occupancy_classification) ?? [];
        bucket.push(row);
        byOccupancy.set(row.occupancy_classification, bucket);
    }
    for (const [occ, rows] of byOccupancy) {
        result.set(occ, mergeEffectiveRows(rows));
    }
    return result;
}
/**
 * The effective requirements for one occupancy classification in one org's scope:
 * global default rows merged with the org's rows (org shadows global; hidden drops).
 */
export async function getEffectiveClassificationRequirements(client, { occupancyClassification, orgId }) {
    const map = await getEffectiveClassificationRequirementsMap(client, {
        occupancyClassifications: [occupancyClassification],
        orgId,
    });
    return map.get(occupancyClassification) ?? [];
}
//# sourceMappingURL=classificationRequirements.js.map